import { put, call, takeEvery, takeLatest, race, take, select } from 'redux-saga/effects';
import * as types from './actionTypes.testMode';
import * as errorSaga from '../errors/saga.errors';
import * as confirmSaga from '../confirm/saga.confirm';
import * as _ from 'lodash';
import Api from '../../api/Api';
import Node from '../../classes/Node';
import TestCases from '../../services/TestCases';
import guid from '../../services/mini/guid';
import applyDrag from '../../services/mini/applyDrag';
import generateQrCode from '../../services/mini/generateQrCode';
import store from '../../store';
import ToasterService from '../../services/ToasterService';
import { push, replace } from 'react-router-redux';
import getConversationId from '../../services/mini/getConversationId';
import { IAddDialogItemWithQrCodeParam, IAddDialogParam, IAddExpectedResponseParam, IAddTextParam, IAddVisualStateParam, IChangeDialogOrderParam, IConnectTestCasesParam, ICopyAllExpectedResponsesParam, ICopyDialogParam, IDeleteAllExpectedResponsesParam, IDeleteDialogItemParam, IDeleteDialogParam, IDeleteExpectedResponseParam, IEditExpectedResponseParam, IGetDialogContentParam, IPasteAllExpectedResponsesParam, IPasteDialogParam, IRefreshAllDialogsParam, IRefreshPhrasesParam, IUpdateTextParam, IUpdateVisualStateParam, IAddDialogItemFromSystemMessageParam, IDisconnectTestCasesParam, IRefreshDialogParam } from './actions.testMode';
import { IBaseAction, IPromiseParam } from '../base';
import { routeInfo } from '../../routeInfo';
import getUrlInfo from '../../services/mini/getUrlInfo';
import { IConversation } from '../../models/IConversation';
import { delay } from 'redux-saga';

declare const alanAudio: any;

var testCasesInstance = {};

function onMessage(message) {
    store.dispatch({
        type: types.ADD_DIALOG_ITEM_WITH_QR_CODE_REQ,
        message
    });
}

let connectedDialogNodes = {};

function onPeerDisconnect() {
    store.dispatch({
        type: types.DEVICE_DISCONNECTED_BY_QR_CODE_REQ,
    });
}

export function* connectTestCases({ projectId, environment, node }: IConnectTestCasesParam) {
    const connection = projectId + '/' + environment;
    testCasesInstance[node.testCaseId] = new TestCases(connection, {
        onParsed: onMessage,
        onPeerAuthorized: ({ status }) => {
            if (status === 'authorized') {
                connectedDialogNodes[node.testCaseId] = status;
            }
        },
        onPeerDisconnect,
        onPeerVisual: onMessage,
        onText: onMessage,
        onCommand: onMessage,
    });
}

export function* disconnectTestCases({ node }: IDisconnectTestCasesParam) {
    if (testCasesInstance[node.testCaseId]) {
        testCasesInstance[node.testCaseId].close();
        yield put({
            type: types.MARK_DIALOG_ACTUAL_REQ,
            dialogId: null,
        });
        yield put({ type: types.GET_DIALOGS_CONTENT_OK, dialogs: {}, dialogTree: null });
    }
}

export function* getDialogsContent({ conversationId, resolve, reject }: IGetDialogContentParam) {
    try {
        let phrases = [];
        let dialogs = {};
        let dialogTree = new Node(conversationId);
        yield put({ type: types.GET_DIALOGS_CONTENT_OK, dialogs: {}, dialogTree: null });

        phrases = yield getTestPhrases({ conversationId });

        dialogTree = dialogTree.getTree(phrases);

        dialogs = dialogTree.toDialogs(dialogs, 0);

        const allNodes = dialogTree.getAllChildren();
        let oldTestCasesFormat = false;

        for (let i = 0; i < allNodes.length; i++) {
            if (allNodes[i].children.length > 1) {
                oldTestCasesFormat = true;
                break;
            }
        }

        if (oldTestCasesFormat) {
            const keys = Object.keys(dialogs);
            let nodesToUpdate = [];
            for (let i = 0; i < keys.length; i++) {
                nodesToUpdate = [...nodesToUpdate, ...dialogs[keys[i]]];
            }
            yield updateTestPhrases({
                conversationId,
                nodesToUpdate,
            });
            yield getDialogsContent({ conversationId, resolve, reject });
            return;
        }

        yield put({ type: types.GET_DIALOGS_CONTENT_OK, dialogs, dialogTree });
        if (resolve) {
            resolve();
        }
    } catch (error) {
        yield errorSaga.putError(error);
        if (reject) {
            reject();
        }
    }
}

export function* addText({ text, conversationId, dialog, dialogTree, curNode, direction, resolve, reject }: IAddTextParam) {
    try {
        let newNode = new Node(guid(), text);
        yield addNode({
            newNode,
            conversationId,
            dialog,
            dialogTree,
            curNode,
            direction
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* updateText({ text, conversationId, dialogTree, curNode, resolve, reject }: IUpdateTextParam) {
    try {
        curNode.request = text;

        yield refreshDialog({
            conversationId,
            dialogTree,
            node: curNode,
            resetDialogContext: false
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* addVisualState({ visualState, conversationId, dialog, curNode, dialogTree, direction, resolve, reject }: IAddVisualStateParam) {
    try {
        let newNode = new Node(guid());
        newNode.request = { visualState };
        yield addNode({
            newNode,
            conversationId,
            dialog,
            dialogTree,
            curNode,
            direction
        });

        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* updateVisualState({ visualState, conversationId, curNode, dialogTree, resolve, reject }: IUpdateVisualStateParam) {
    try {
        curNode.request = { visualState };
        yield refreshDialog({
            conversationId, dialogTree, node: curNode, resetDialogContext: false
        });

        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* deleteDialogItem({ dialogTree, node, conversationId }: IDeleteDialogItemParam) {
    try {
        let nodesToDelete;
        let nodesToUpdate = [];

        let confirmResult;

        if (node.hasChildren()) {
            confirmResult = yield confirmSaga.confirm({
                title: 'Warning',
                message: "Are you sure you want to delete this phrase and all the following phrases?",
                okBtnLabel: 'Delete all',
                midBtnLabel: 'Delete one',
            });
        } else {
            confirmResult = yield confirmSaga.confirm({
                title: 'Warning',
                message: "Are you sure you want to delete this phrase?",
                okBtnLabel: 'Delete',
            });
        }
        let parentNode;
        let firstNode = node.parentId !== '' ? node.getAllParentNodes()[0] : node;
        const dialogIndex = firstNode.childIndex || 0;


        if (node.parentId) {
            parentNode = dialogTree.findNode(node.parentId);
        } else {
            parentNode = dialogTree;
        }

        if (confirmResult.okBtnAction) {
            parentNode.removeChildrenById(node.id);
            nodesToDelete = [node, ...node.getAllChildren()];
            if (!node.parentId) {
                let newNode = new Node();
                newNode.markAsStartConversationNode();
                newNode.setChildIndex(node.childIndex);
                parentNode.children.splice(node.childIndex, 0, newNode);
            }
        }
        if (confirmResult.midBtnAction) {
            nodesToDelete = [node];
            parentNode.removeChildrenById(node.id, true);
            nodesToUpdate = [
                ...(parentNode.parentId === null ? [] : [parentNode]),
                ...parentNode.children];
        }
        if (confirmResult.cancelBtnAction) {
            return;
        }

        yield updateTestPhrases({
            conversationId,
            nodesToUpdate,
            nodesToDelete
        });

        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });

        yield refreshDialog({
            conversationId,
            node: dialogTree.children[dialogIndex],
            dialogTree,
            resetDialogContext: true,
        });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* addDialogItemFromSystemMessage({ text, category, data, reqId, meta, conversationId, dialog, dialogTree, resolve, reject }: IAddDialogItemFromSystemMessageParam) {
    try {
        let newNode = new Node(guid());
        let sourceNode = dialog[dialog.length - 1];
        let phrases = [];
        const state = store.getState();
        const { actualDialogId } = state.testMode;
        let isActual = actualDialogId === dialog[0].id;

        if (!isActual) {
            yield refreshDialog({ conversationId, dialogTree, node: dialog[0], resetDialogContext: false });
        }

        if ((category === 'request' && text) || (category === 'visualState' && data)) {
            if (!sourceNode.request) {
                sourceNode.request = category === 'request' ? { text } : { visualState: data };
                sourceNode.reqId = reqId;
                sourceNode.meta = meta;
                phrases = [sourceNode];
            } else {
                newNode.request = category === 'request' ? { text } : { visualState: data };
                newNode.reqId = reqId;
                newNode.meta = meta;
                sourceNode.addChild(newNode);
                phrases = [sourceNode, newNode];
            }
        }

        if ((category === 'response' && text) || (category === 'command' && data)) {
            if (category === 'command') {
                sourceNode.response = [...sourceNode.response, { data }];
                phrases = [sourceNode];
            } else {
                if (sourceNode.reqId === reqId) {
                    sourceNode.response = [...sourceNode.response, { text, ...meta }];
                    phrases = [sourceNode];
                }
            }
        }
        if (phrases.length > 0) {
            yield updateDialogTree({ dialogTree, phrases, conversationId });
        }

        if (resolve) {
            resolve();
        }
    } catch (error) {
        if (reject) {
            reject();
        }
        yield errorSaga.putError(error);
    }
}

export function* genereateQrCodeForDialog({ resolve, reject }: IPromiseParam) {
    // try {
    //     let qrCodeString = testCasesInstance ? generateQrCode(testCasesInstance.getSettings()) : '';
    //     resolve(qrCodeString);
    // } catch (error) {
    //     if (reject) {
    //         reject();
    //     }
    //     yield errorSaga.putError(error);
    // }
}

export function* addDialogItemWithQrCode({ message }: IAddDialogItemWithQrCodeParam) {
    const state = store.getState();
    const { dialogTree, activeDialogIdForQrCode } = state.testMode;
    if (activeDialogIdForQrCode === null) return;
    const conversationId = getConversationId(state.projectsInfo.currentProject.conversations);
    const { reqId, text, data } = message;
    const rootNode = dialogTree.findNode(activeDialogIdForQrCode);
    const dialog = [rootNode, ...rootNode.getAllChildren()];
    let category;

    if (message.type === 'text' && message.my) {
        category = 'request';
    } else if (message.type === 'text' && !message.my) {
        category = 'response';
    } else if (message.type === 'visualState') {
        category = 'visualState';
    }

    let meta = {} as any;

    if (message.pLoc) {
        meta.pLoc = message.pLoc;
    }
    if (message.rLoc) {
        meta.rLoc = message.rLoc;
    }

    yield addDialogItemFromSystemMessage({
        text,
        category,
        data,
        reqId,
        conversationId,
        dialog,
        dialogTree,
        meta
    } as IAddDialogItemFromSystemMessageParam);
}

export function* connectDeviceByQrCode() {
    window.dispatchEvent(new CustomEvent("HIDE_QR_CODE_POPUP"));
    store.dispatch({ type: types.DEVICE_CONNECTED_BY_QR_CODE_OK });
    ToasterService.showInfo({ title: "Mobile device connected" });
}

export function* disconnectDeviceByQrCode() {
    store.dispatch({ type: types.DEVICE_DISCONNECTED_BY_QR_CODE_OK });
    ToasterService.showInfo({ title: "Mobile device disconnected" });
}

export function* addNode({ newNode, conversationId, dialog, dialogTree, curNode, direction }) {
    try {
        let nodes;
        let nodeToSave;
        const state = store.getState();
        const { actualDialogId } = state.testMode;
        let curDialogId = dialog[0].id;
        let testCaseId = dialog[0].testCaseId;
        let curDialogNodeCount = dialog[0].getAllChildren().length + 2;
        let isActual = actualDialogId === curDialogId;

        if (!isActual) {
            yield call(resetContext, testCaseId);
            yield put({ type: types.START_REFRESHING_DIALOG_COUNTER_OK, conversationId, dialogId: curDialogId, count: curDialogNodeCount });
        }

        if (!curNode) { // add new node in the end of the dialog
            let destNode = dialog[0];
            // dialog is empty: there is a node in the dialog, but request is empty
            if (_.isEmpty(destNode.request)) {
                newNode.markAsStartConversationNode(conversationId);
                newNode.setChildIndex(dialogTree.children.length - 1);
                newNode.testCaseId = testCaseId;
                destNode.reFillNode(newNode, true);
                nodeToSave = destNode;
                nodes = [nodeToSave];
            } else {
                let lastNode = dialog[dialog.length - 1];
                lastNode.addChild(newNode);
                nodeToSave = newNode;
                nodes = [nodeToSave];
            }
            if (isActual) {
                nodes = [nodeToSave];
            } else {
                nodes = newNode.getAllParentNodes();
            }
        } else {
            let parentNode;
            let curNodeIndex;

            yield put({ type: types.START_REFRESHING_DIALOG_COUNTER_OK, conversationId, dialogId: curDialogId, count: curDialogNodeCount });

            if (direction === 'before') {
                parentNode = !curNode.parentId ? dialogTree : dialogTree.findNode(curNode.parentId);
                curNodeIndex = _.findIndex(parentNode.children, { id: curNode.id });
            } else {
                parentNode = curNode;
                curNodeIndex = 0;
            }

            var nodeToMoveDown = parentNode.children.splice(curNodeIndex, 1)[0];
            const beginingOfTheDialog = !curNode.parentId && direction === 'before';

            if (beginingOfTheDialog) {
                newNode.markAsStartConversationNode(conversationId);
            } else {
                newNode.parentId = parentNode.id;
            }

            parentNode.children.splice(curNodeIndex, 0, newNode);
            newNode.root = dialogTree;
            newNode.setChildIndex(curNodeIndex);
            newNode.testCaseId = testCaseId;
            nodeToMoveDown.conversationId = null;
            newNode.addChild(nodeToMoveDown);
            let nodeToUpdate = parentNode.children[curNodeIndex];
            if (beginingOfTheDialog) {
                nodes = [nodeToUpdate, ...nodeToUpdate.getAllChildren()];
            } else {
                nodes = [
                    ...parentNode.getAllParentNodes(),
                    parentNode,
                    ...parentNode.getAllChildren()
                ];
            }
            nodeToSave = newNode;
        }
        curDialogId = dialog[0].id;
        const phrases = yield call(fillPhraseResponses, curDialogId, nodes, nodeToSave, testCaseId, conversationId);
        yield updateDialogTree({ dialogTree, phrases, conversationId });
        curDialogId = nodeToSave.parentId === '' ? nodeToSave.id : curDialogId;
        yield put({
            type: types.MARK_DIALOG_ACTUAL_REQ,
            dialogId: curDialogId,
        });

        yield put({ type: types.FINISH_REFRESHING_DIALOG_COUNTER_OK, dialogId: curDialogId });

    } catch (error) {
        yield errorSaga.putError(error);
    }
}


export function* addDialog({ dialogTree, conversationId }: IAddDialogParam) {
    try {
        let newNode = new Node();
        newNode.markAsStartConversationNode(conversationId);
        newNode.fillTestCaseId();
        dialogTree.addChild(newNode);
        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
        yield put({ type: types.MARK_DIALOG_ACTUAL_REQ, dialogId: newNode.id });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* copyDialog({ node }: ICopyDialogParam) {
    try {
        const tempNode = new Node();
        tempNode.fillTestCaseId();

        const nodesToCopy = [node, ...node.getAllChildren()];
        for (let i = 0; i < nodesToCopy.length; i++) {
            node.testCaseId = tempNode.testCaseId;
        }

        yield put({
            type: types.COPY_DIALOG_OK,
            copiedNodes: nodesToCopy.map(node => node.toPhrase()),
        });

        ToasterService.showInfo({ title: 'Dialog was copied' });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* pasteDialog({ conversationId, dialogTree, phrases, resolve, reject }: IPasteDialogParam) {
    try {
        const newNode = new Node();
        const nodeIndex = dialogTree.children.length;
        const allChilds = [...phrases];
        const copiedChilds = [];
        newNode.reFillNode(phrases[0]);
        newNode.id = guid();
        dialogTree.children.splice(nodeIndex, 0, ...[newNode]);

        for (let i = 0; i < dialogTree.children.length; i++) {
            dialogTree.children[i].setChildIndex(i);
        }

        for (let i = 1; i < allChilds.length; i++) {
            const tempNode = new Node();
            tempNode.reFillNode(allChilds[i]);
            tempNode.id = guid();
            copiedChilds.push(tempNode);
        }

        if (copiedChilds.length > 0) {
            newNode.addChild(copiedChilds[0]);
            copiedChilds[0].parentId = newNode.id;
        }
        for (let i = 0; i < copiedChilds.length - 1; i++) {
            copiedChilds[i].addChild(copiedChilds[i + 1]);
            copiedChilds[i + 1].parentId = copiedChilds[i].id;
        }

        yield updateTestPhrases({
            conversationId,
            nodesToUpdate: [newNode, ...copiedChilds],
        });

        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });

        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
    }
}


export function* deleteDialog({ dialogTree, node, conversationId }: IDeleteDialogParam) {
    try {
        let nodesToDelete;
        let nodesToUpdate = [];

        const { cancelBtnAction } = yield confirmSaga.confirm({
            title: 'Warning',
            message: "Are you sure you want to delete this test dialog?",
            okBtnLabel: 'Delete',
        });
        if (cancelBtnAction) {
            return;
        }
        dialogTree.removeChildrenById(node.id);

        if (testCasesInstance[node.testCaseId]) {
            delete testCasesInstance[node.testCaseId];
        }

        nodesToDelete = [node, ...node.getAllChildren()];

        yield updateTestPhrases({
            conversationId,
            nodesToUpdate,
            nodesToDelete
        });

        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* changeDialogOrder({ conversationId, dialogTree, addedIndex, removedIndex, resolve, reject }: IChangeDialogOrderParam) {
    try {
        const reorderedNodes = applyDrag(dialogTree.children, addedIndex, removedIndex);
        dialogTree.children = reorderedNodes;
        for (let i = 0; i < reorderedNodes.length; i++) {
            reorderedNodes[i].setChildIndex(i);
        }

        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });

        yield updateTestPhrases({
            conversationId,
            nodesToUpdate: reorderedNodes
        });

    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* addExpectedResponse({ conversationId, dialogTree, node, response, resolve, reject }: IAddExpectedResponseParam) {
    try {
        node.setExpectedResponse(response);
        if (response.length > 0) {
            yield updateTestPhrases({
                conversationId,
                nodesToUpdate: [...node.getAllParentNodes(), node]
            });
        }
        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* copyAllExpectedResponses({ node, resolve, reject }: ICopyAllExpectedResponsesParam) {
    try {
        yield put({
            type: types.COPY_EXPECTED_RESPONSE_OK,
            copiedExpectedResponses: {
                expectedResponses: node.visualProperties?.expectedResponses,
                condition: node.visualProperties?.condition,
            }
        });
        ToasterService.showInfo({ title: 'Expected response was copied to buffer' });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* pasteAllExpectedResponses({ conversationId, dialogTree, node, copiedExpectedResponses, resolve, reject }: IPasteAllExpectedResponsesParam) {
    try {
        if (copiedExpectedResponses) {
            node.setExpectedResponse(copiedExpectedResponses.expectedResponses);
        }

        yield updateTestPhrases({
            conversationId,
            nodesToUpdate: [...node.getAllParentNodes(), node]
        });
        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* editExpectedResponse({ conversationId, dialogTree, node, response, resolve, reject }: IEditExpectedResponseParam) {
    try {
        node.setExpectedResponse(response);
        yield updateTestPhrases({
            conversationId,
            nodesToUpdate: [...node.getAllParentNodes(), node]
        });
        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* deleteExpectedResponse({ conversationId, dialogTree, node, response, resolve, reject }: IDeleteExpectedResponseParam) {
    try {
        node.deleteExpectedResponse(response);
        yield updateTestPhrases({
            conversationId,
            nodesToUpdate: [...node.getAllParentNodes(), node]
        });
        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* deleteAllExpectedResponses({ conversationId, dialogTree, node, resolve, reject }: IDeleteAllExpectedResponsesParam) {
    try {
        node.deleteAllExpectedResponses();
        yield updateTestPhrases({
            conversationId,
            nodesToUpdate: [...node.getAllParentNodes(), node]
        });
        yield put({
            type: types.GET_DIALOGS_CONTENT_OK,
            dialogs: dialogTree.toDialogs({}, 0),
            dialogTree
        });
        resolve();
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

export function* refreshDialog({ conversationId, dialogTree, node, resetDialogContext, keepRefreshCounter, resolve, reject }: IRefreshPhrasesParam) {
    try {
        yield put({ type: types.START_REFRESHING_DIALOG_COUNTER_OK, conversationId, dialogId: node.id, count: node.getAllChildren().length + 1 });

        const nodes =
            [
                ...node.getAllParentNodes(),
                node,
                ...node.getAllChildren()
            ];

        if (resetDialogContext) {
            yield resetContext(node.testCaseId);
        }

        if (node.hasFilledResuest()) {
            const phrases = yield call(fillPhraseResponses, node.id, nodes, node, node.testCaseId, conversationId);
            yield updateDialogTree({ dialogTree, phrases, conversationId });
        } else {
            // fake timeout to improve visual UI of the refresh
            yield new Promise(resolve => {
                setTimeout(resolve, 200);
            });
        }

        if (!node.parentId) {
            yield put({ type: types.MARK_DIALOG_ACTUAL_REQ, dialogId: node.id });
        }

        if (keepRefreshCounter !== true) {
            yield put({ type: types.FINISH_REFRESHING_DIALOG_COUNTER_OK, dialogId: node.id });
        }

        yield put({ type: types.REFRESH_PHRASES_OK });

        if (resolve) {
            resolve();
        }
    } catch (error) {
        if (reject) {
            reject();
        }
        yield errorSaga.putError(error);
    }
}

function* checkDialogsConnections(connectionIds) {
    const checkInterval = 1000;
    const startCheckingTs = Date.now();

    while (true) {
        const allConnected = connectionIds.every(id => connectedDialogNodes[id] === 'authorized');

        if (Date.now() - startCheckingTs > 60000) {
            return false;
        }

        if (allConnected) {
            // console.info('All dialogs connected');
            return true;
        }
        // console.info('Check dialogs connections');
        yield delay(checkInterval);
    }
}

function* runAllDialogsInConversation({ conversation, scriptName, projectName, reloadDialog }: {
    conversation: IConversation
    scriptName: string;
    projectName: string;
    reloadDialog?: boolean;
}) {
    try {

        const conversationId = conversation.id;

        if (reloadDialog !== false) {
            yield put(push(routeInfo.projectTests.url(projectName, scriptName, conversation.name)));
            yield delay(3000);
        }
        
        const state = store.getState();
        const dialogTree = state.testMode.dialogTree;

        yield put({ type: types.UPDATE_REFRESH_ALL_DIALOGS_COUNTER_OK, all: dialogTree.children?.length, count: 0, conversationName: conversation.name, status: 'started' });

        let connectionStatus;

        if (reloadDialog !== false) {
            connectionStatus = yield checkDialogsConnections(dialogTree.children?.map(el => el.testCaseId));
        }
        if (connectionStatus || reloadDialog === false) {
            for (let i = 0; i < dialogTree.children.length; i++) {
                yield refreshDialog({ conversationId, dialogTree, node: dialogTree.children[i], keepRefreshCounter: true, resetDialogContext: false });
                yield put({ type: types.UPDATE_REFRESH_ALL_DIALOGS_COUNTER_OK, count: i + 1 });
            }
            yield put({ type: types.UPDATE_REFRESH_ALL_DIALOGS_COUNTER_OK, all: 0, count: 0, conversationName: conversation.name, status: 'completed' });
        }

    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* runDialog({ conversationId, resolve, reject }: IRefreshDialogParam) {
    try {
        const state = store.getState();
        const { scriptName, projectName, conversationName } = getUrlInfo();
        const conversations = state.projectsInfo.currentProject.conversations;

        connectedDialogNodes = {};

        for (let i = 0; i < conversations.length; i++) {
            const conversation = conversations[i];
            if (conversation.id === conversationId) {
                yield runAllDialogsInConversation({
                    conversation,
                    scriptName,
                    projectName,
                    reloadDialog: conversationName !== conversation.name
                });
                yield collectDialogInfo({ conversation });
                yield put({ type: types.STOP_REFRESHING_ALL_DIALOGS_OK });
                break;
            }
        }
    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}

interface ICollectDialogInfoParam {
    conversation: IConversation
}

export function* collectDialogInfo({ conversation }: ICollectDialogInfoParam) {
    try {
        const conversationId = conversation.id;
        yield put({ type: types.COLLECT_DIALOG_INFO_REQ, conversationId });

        let dialogs = {};
        let dialogTree = new Node(conversationId);

        const phrases = yield getTestPhrases({ conversationId });

        dialogTree = dialogTree.getTree(phrases);

        dialogs = dialogTree.toDialogs(dialogs, 0);

        let passed = 0;
        let failed = 0;

        const dialogsKeys = dialogs ? Object.keys(dialogs) : [];

        for (let i = 0; i < dialogsKeys.length; i++) {
            const dialog = dialogs[dialogsKeys[i]];
            const isPassed = dialog[0]?.isPassedRecursively();
            if (isPassed === true) {
                passed = passed + 1;
            } else if (isPassed === false) {
                failed = failed + 1;
            }
        }

        yield put({
            type:
                types.COLLECT_DIALOG_INFO_OK,
            conversationId,
            passed,
            failed
        });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* runAllDialogs({ resolve, reject }: IRefreshAllDialogsParam) {
    try {
        connectedDialogNodes = {};

        const state = store.getState();
        const { scriptName, projectName } = getUrlInfo()
        const conversations = state.projectsInfo.currentProject.conversations;

        for (let i = 0; i < conversations.length; i++) {
            const conversation = conversations[i];
            yield runAllDialogsInConversation({
                conversation,
                scriptName,
                projectName
            });
            yield collectDialogInfo({ conversation });
        }

        yield put({ type: types.REFRESH_ALL_DIALOGS_OK });

        ToasterService.showInfo({ title: 'Test conversations for this project have been successfully completed. You can view the results in the "Test Cases" panel.' });

    } catch (error) {
        reject();
        yield errorSaga.putError(error);
    }
}
export function* stopAllDialogs() {
    yield put({ type: types.STOP_REFRESHING_ALL_DIALOGS_OK });
}

export function* stopDialog() {
    yield put({ type: types.STOP_REFRESHING_ALL_DIALOGS_OK });
}

export function* stopAudio() {
    try {
        alanAudio.stop();
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

function* updateTestPhrases({
    conversationId,
    nodesToUpdate,
    nodesToDelete = []
}) {
    try {
        let phrasesToUpdate = nodesToUpdate.map(node => formatPhraseBeforeSave(node));
        for (let i = 0; i < phrasesToUpdate.length; i++) {
            if (_.isObject(phrasesToUpdate[i].request)) {
                phrasesToUpdate[i].request = JSON.stringify(phrasesToUpdate[i].request);
            }
        }

        yield call(
            Api.updateTestPhrases,
            conversationId,
            phrasesToUpdate,
            nodesToDelete.map(node => node.id));
    } catch (error) {
        yield errorSaga.putError(error);
    }
}
export function* updateDialogTree({ dialogTree, phrases, conversationId }) {
    try {
        let nodesFilteredByMeta = phrases
            .filter(phrase => {
                const hasErrorsInResponses = !!_.find(phrase?.response, { error: true });
                if (phrase.request?.text !== '' && !hasErrorsInResponses) {
                    return phrase.meta !== '{}';
                }
                return true;
            });

        let nodesToUpdate = nodesFilteredByMeta
            .filter(node => {
                return !!node.reqId;
            });

        let nodesWithoutReqId = nodesFilteredByMeta
            .filter(node => {
                return !node.reqId;
            });

        for (let i = 0; i < nodesWithoutReqId.length; i++) {
            const tempNode = nodesWithoutReqId[i];
            if (!_.find(nodesToUpdate, { id: tempNode.id })) {
                nodesToUpdate.push(tempNode);
            }
        }

        for (var i = 0; i < nodesToUpdate.length; i++) {
            let tempNode = dialogTree.findNode(nodesToUpdate[i].id);
            tempNode.reFillNode({ ...tempNode, ...nodesToUpdate[i] });
        }

        const state = store.getState();
        const { dialogTree: dialogTreeInState } = state.testMode;
        if (dialogTreeInState?.id === conversationId) {
            yield put({
                type: types.GET_DIALOGS_CONTENT_OK,
                dialogs: dialogTree.toDialogs({}, 0),
                dialogTree
            });
        }

        yield updateTestPhrases({
            conversationId,
            nodesToUpdate
        });
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

function resetContext(testCaseId) {
    return new Promise<void>((resolve, reject) => {
        if (testCasesInstance[testCaseId]) {
            testCasesInstance[testCaseId].resetContext().then(
                () => {
                    resolve();
                },
                () => {
                    reject();
                });
        }
    });
}

function updateDialogRefreshCounter({ node, chainId }) {

    const state = store.getState();

    if (state?.testMode?.refreshingIsInProcess) {
        store.dispatch({
            type: types.UPDATE_REFRESHING_DIALOG_COUNTER_OK,
            dialogId: chainId,
            nodeId: node.id
        });
    }
}

function fillPhraseResponses(dialogId, nodes, node, testCaseId, conversationId) {
    return new Promise((resolve, reject) => {
        if (testCasesInstance[testCaseId]) {
            const phrases = nodes.map(node => node.toPhrase());
            const phrase = node.toPhrase();
            let fillResponsesCbTimer = null;
            store.dispatch({
                type: types.START_REFRESHING_DIALOG_COUNTER_OK,
                conversationId,
                dialogId: dialogId,
                count: nodes.length
            });

            testCasesInstance[testCaseId].on('nodeRefreshEnd', updateDialogRefreshCounter);
            testCasesInstance[testCaseId].fillResponses(dialogId, formatPhrasesForTestCasesCall([...phrases, phrase]), phrase, (resultPhrases) => {
                clearTimeout(fillResponsesCbTimer);
                fillResponsesCbTimer = setTimeout(() => {
                    resolve(resultPhrases);
                    testCasesInstance[testCaseId].off('nodeRefreshEnd', updateDialogRefreshCounter);
                }, 1000);
            });
        }
    });
}

export function* getTestPhrases({ conversationId }) {
    try {
        const phrases = yield call(Api.getTestPhrases, conversationId);

        for (var i = 0; i < phrases.length; i++) {
            try {
                phrases[i].request = JSON.parse(phrases[i].request);
            } catch (e) {
                phrases[i].request = { text: '' };
            }
        }

        return phrases;
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

function formatPhraseBeforeSave(phrase) {
    return {
        id: phrase.id,
        parentId: phrase.name ? '' : phrase.parentId,
        request: _.isObject(phrase.request) ? phrase.request : { text: phrase.request },
        response: _.isObject(phrase.response) ? JSON.stringify(phrase.response) : phrase.response,
        meta: (_.isString(phrase.meta) && phrase.meta !== '{}') ?
            phrase.meta :
            JSON.stringify({
                pLoc: phrase.meta ? phrase.meta.pLoc : {},
            }),
        visualProperties: _.isObject(phrase.visualProperties) ?
            JSON.stringify(phrase.visualProperties) :
            phrase.visualProperties
    };
}

function formatPhrasesForTestCasesCall(phrases) {
    return phrases.map((phrase) => {
        try {
            phrase.response = JSON.parse(phrase.response);
            if (!phrase.request.text) {
                phrase.request.text = "";
            }
        } catch (e) {

        }
        return phrase;
    });
}


export function* watch() {
    yield takeEvery<IConnectTestCasesParam & IBaseAction>(types.CONNECT_TEST_CASES_REQ, connectTestCases);
    yield takeEvery<IDisconnectTestCasesParam & IBaseAction>(types.DISCONNECT_TEST_CASES_REQ, disconnectTestCases);
    yield takeEvery<IGetDialogContentParam & IBaseAction>(types.GET_DIALOGS_CONTENT_REQ, getDialogsContent);
    yield takeEvery<IAddTextParam & IBaseAction>(types.ADD_TEXT_DIALOG_ITEM_REQ, addText);
    yield takeEvery<IUpdateTextParam & IBaseAction>(types.UPDATE_TEXT_DIALOG_ITEM_REQ, updateText);
    yield takeEvery<IAddVisualStateParam & IBaseAction>(types.ADD_VISUAL_STATE_DIALOG_ITEM_REQ, addVisualState);
    yield takeEvery<IUpdateVisualStateParam & IBaseAction>(types.UPDATE_VISUAL_STATE_DIALOG_ITEM_REQ, updateVisualState);
    yield takeEvery<IDeleteDialogItemParam & IBaseAction>(types.DELETE_DIALOG_ITEM_REQ, deleteDialogItem);
    yield takeLatest<IAddDialogItemFromSystemMessageParam & IBaseAction>(types.ADD_DIALOG_ITEM_FROM_SYSTEM_MSG_REQ, addDialogItemFromSystemMessage);
    yield takeEvery<IPromiseParam & IBaseAction>(types.DEVICE_CONNECTED_BY_QR_CODE_REQ, connectDeviceByQrCode);
    yield takeEvery<IPromiseParam & IBaseAction>(types.DEVICE_DISCONNECTED_BY_QR_CODE_REQ, disconnectDeviceByQrCode);
    yield takeEvery<IPromiseParam & IBaseAction>(types.GENERATE_QR_CODE_REQ, genereateQrCodeForDialog);
    yield takeEvery<IAddDialogItemWithQrCodeParam & IBaseAction>(types.ADD_DIALOG_ITEM_WITH_QR_CODE_REQ, addDialogItemWithQrCode);
    yield takeEvery<IAddDialogParam & IBaseAction>(types.ADD_DIALOG_REQ, addDialog);
    yield takeEvery<ICopyDialogParam & IBaseAction>(types.COPY_DIALOG_REQ, copyDialog);
    yield takeEvery<IPasteDialogParam & IBaseAction>(types.PASTE_DIALOG_REQ, pasteDialog);
    yield takeEvery<IDeleteDialogParam & IBaseAction>(types.DELETE_DIALOG_REQ, deleteDialog);
    yield takeEvery<IChangeDialogOrderParam & IBaseAction>(types.CHANGE_DIALOG_ORDER_REQ, changeDialogOrder);
    yield takeEvery<IAddExpectedResponseParam & IBaseAction>(types.ADD_EXPECTED_RESPONSE_REQ, addExpectedResponse);
    yield takeEvery<ICopyAllExpectedResponsesParam & IBaseAction>(types.COPY_EXPECTED_RESPONSE_REQ, copyAllExpectedResponses);
    yield takeEvery<IPasteAllExpectedResponsesParam & IBaseAction>(types.PASTE_EXPECTED_RESPONSE_REQ, pasteAllExpectedResponses);
    yield takeEvery<IEditExpectedResponseParam & IBaseAction>(types.EDIT_EXPECTED_RESPONSE_REQ, editExpectedResponse);
    yield takeEvery<IDeleteExpectedResponseParam & IBaseAction>(types.DELETE_EXPECTED_RESPONSE_REQ, deleteExpectedResponse);
    yield takeEvery<IDeleteAllExpectedResponsesParam & IBaseAction>(types.DELETE_ALL_EXPECTED_RESPONSES_REQ, deleteAllExpectedResponses);
    yield takeEvery(types.STOP_AUDIO_IN_TEST_MODE_REQ, stopAudio);

    yield takeEvery<IRefreshAllDialogsParam & IBaseAction>([types.REFRESH_ALL_DIALOGS_REQ], function* (...args) {
        yield race({
            task: call(runAllDialogs, ...args),
            cancel: take(types.STOP_REFRESHING_ALL_DIALOGS_REQ)
        })
    });

    yield takeEvery<IRefreshPhrasesParam & IBaseAction>([types.REFRESH_PHRASES_REQ], function* (...args) {
        yield race({
            task: call(refreshDialog, ...args),
            cancel: take(types.STOP_REFRESHING_ONE_DIALOG_REQ)
        })
    });

    yield takeEvery(types.STOP_REFRESHING_ALL_DIALOGS_REQ, stopAllDialogs);

    yield takeEvery<IRefreshDialogParam & IBaseAction>([types.REFRESH_DIALOGS_IN_CONVERSATION_REQ], function* (...args) {
        yield race({
            task: call(runDialog, ...args),
            cancel: take(types.STOP_REFRESHING_DIALOG_REQ)
        })
    });

    yield takeEvery(types.STOP_REFRESHING_DIALOG_REQ, stopDialog);
}